import { useState, useRef, useCallback } from "react";
import { ChunkQueue } from "../helpers/chunkQueue";

interface UseMediaRecorderOptions {
  chunkQueue: ChunkQueue;
  onStopRecording?: () => void;
  displayStream: MediaStream | null;
}

interface MediaRecorderState {
  isRecording: boolean;
  error: string | null;
}

export const useMediaRecorder = ({
  chunkQueue,
  onStopRecording,
  displayStream,
}: UseMediaRecorderOptions) => {
  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    error: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamsRef = useRef<{ screen?: MediaStream; mic?: MediaStream }>({});

  const startRecording = useCallback(async () => {
    try {
      setState({ isRecording: false, error: null });

      if (!displayStream) {
        throw new Error("Display stream not available");
      }

      // Use the provided display stream instead of requesting again
      const stream = displayStream;

      // Get microphone
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      // Mix audio streams
      const audioContext = new AudioContext();
      await audioContext.resume();

      const destination = audioContext.createMediaStreamDestination();
      const merger = audioContext.createChannelMerger(2);

      const screenTracks = stream.getAudioTracks();
      if (screenTracks.length > 0) {
        const screenSource = audioContext.createMediaStreamSource(
          new MediaStream(screenTracks)
        );
        const screenGain = audioContext.createGain();
        screenSource.connect(screenGain);
        screenGain.connect(merger, 0, 0);
      }

      const micSource = audioContext.createMediaStreamSource(micStream);
      const micGain = audioContext.createGain();
      micSource.connect(micGain);
      micGain.connect(merger, 0, 1);

      merger.connect(destination);

      const videoTrack = stream.getVideoTracks()[0];
      const finalStream = new MediaStream([
        videoTrack,
        ...destination.stream.getAudioTracks(),
      ]);

      streamsRef.current = { screen: stream, mic: micStream };

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(finalStream, {
        mimeType: "video/webm; codecs=vp9",
        audioBitsPerSecond: 96000,
        videoBitsPerSecond: 750000,
      });

      mediaRecorder.ondataavailable = async (e) => {
        if (e.data && e.data.size > 0) {
          await chunkQueue.enqueueBlobData(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        stopRecording();
        onStopRecording?.();
      };

      mediaRecorder.start(1000); // Slice data every 1000ms
      mediaRecorderRef.current = mediaRecorder;

      setState({ isRecording: true, error: null });
      console.log("MediaRecorder started. Slicing data every 1000ms.");

      return finalStream;
    } catch (error) {
      console.error("Recording failed to start:", error);
      setState({
        isRecording: false,
        error:
          error instanceof Error ? error.message : "Failed to start recording",
      });
      return null;
    }
  }, [chunkQueue, onStopRecording, displayStream]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    // Stop all tracks
    Object.values(streamsRef.current).forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    });

    streamsRef.current = {};
    setState({ isRecording: false, error: null });
  }, []);

  return {
    startRecording,
    stopRecording,
    isRecording: state.isRecording,
    error: state.error,
  };
};
